import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, User, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { PrimaryButton } from './buttons';
import { login, UserInfo } from '../utils/authApi';
import { toast } from 'react-hot-toast';

export type UserProfile = 'diretor' | 'vendedor';

interface LoginScreenProps {
    onLogin: (profile: UserProfile, userInfo: UserInfo) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logoError, setLogoError] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isFormValid) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await login({ email, password });

            if (response.success && response.data) {
                const { user } = response.data;
                toast.success(`Login realizado como ${user.profile_type === 'diretor' ? 'Diretor' : 'Vendedor'}`);
                onLogin(user.profile_type, user);
            } else {
                throw new Error(response.error || 'Erro desconhecido');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Erro ao fazer login';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = email.trim() && password.trim();

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-1 sm:px-6 py-8">
            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-dexco-white rounded-xl shadow-lg p-6 sm:p-8 border border-gray-200"
                >
                    {/* Header */}
                    <div className="text-center mb-8">
                        {/* Dexco Logo */}
                        <div className="flex justify-center mb-6">
                            {!logoError ? (
                                <img
                                    src="/wordmark.svg"
                                    alt="Dexco"
                                    className="h-12 w-auto"
                                    onError={() => setLogoError(true)}
                                />
                            ) : (
                                <div className="text-3xl font-display font-bold text-dexco-green-primary">
                                    DEXCO
                                </div>
                            )}
                        </div>

                        <h1 className="text-2xl font-display font-bold text-dexco-text-dark mb-2">
                            Acesso ao Sistema
                        </h1>
                        <p className="text-gray-600 font-body">
                            Entre com suas credenciais para continuar
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User className="w-4 h-4 inline mr-2" />
                                Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent transition-all"
                                placeholder="Digite seu email"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Lock className="w-4 h-4 inline mr-2" />
                                Senha
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent transition-all"
                                placeholder="Digite sua senha"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </motion.div>
                        )}

                        {/* Submit Button */}
                        <PrimaryButton
                            type="submit"
                            disabled={!isFormValid || isLoading}
                            leftIcon={isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                            className="w-full"
                        >
                            {isLoading ? 'Entrando...' : 'Entrar'}
                        </PrimaryButton>
                    </form>

                    {/* Footer */}
                    <div className="mt-8 text-center text-xs text-gray-500">
                        Sistema de Priorização - DEXCO ERP
                    </div>
                </motion.div>
            </div>
        </div>
    );
}; 